import React, { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../../../stores/app'
import { DashboardTableCard } from '../../components/dashboard-table-card'
import { DomainSearch, domainMatchesSearchTerm } from '../../components/domain-search'
import { NoDomains } from '../../components/domain-empty-states'
import { SectionError } from '../../components/section-error'
import { TABLE_PLACEHOLDER_ROW_COUNT } from '../../components/table-placeholder-rows'
import { DOMAINS_TABLE_ROW_HEIGHT } from '../../components/table-row-heights'
import { TableSkeleton } from '../../components/table-skeleton'
import { TableSectionWell } from '../../components/section-well'
import { useOpenInDomainContext } from '../../data/use-open-in-domain-context'
import { DashboardDomain, GoodDirection } from '../../types'
import { DailyRow } from '../../math/series'
import { DateWindow, comparisonWindowFor } from '../../math/windows'
import { DashboardTemplate, TEMPLATE_SLOTS } from '../../template'
import { currenciesIn } from '../headline/headline-model'
import { SINGLE_CURRENCY_CELL_KEYS, buildDomainRows, buildTotalsRow, sortDomainRows } from './domains-model'
import {
    DOMAINS_TABLE_CLASS_NAME,
    DOMAINS_TABLE_SCROLL,
    DomainsSort,
    DomainsTable,
    domainsColumnFrames,
} from './domains-table'

/**
 * The preferences service keeps only the fields its account-scoped struct declares, plus `extras`: a flat record
 * whose values are scalars or lists of scalars. A nested object under any other key is accepted and then dropped,
 * so the pins live in `extras` under one flat, feature-prefixed key, as the platform's other stored settings do.
 */
export const PINNED_DOMAIN_IDS_PREFERENCE_PATH = ['extras', 'org_dashboard:pinned_domain_ids']

/** The stored pinned domain ids. A stored value that is not a list of whole numbers is not trusted, and pins nothing. */
export const pinnedDomainIdsFrom = (stored: unknown): number[] =>
    Array.isArray(stored) ? stored.filter(Number.isInteger) : []

/** The Total row holds a label under the domain and a figure under every numeric column; Context has no total. */
const domainsTotalsShapes = (template: DashboardTemplate): Record<string, 'label' | 'figure'> => ({
    domain: 'label',
    ...Object.fromEntries(
        TEMPLATE_SLOTS[template].domainsColumns.filter((key) => key !== 'context').map((key) => [key, 'figure']),
    ),
})

/** Used instead of a revenue column's default when currencies are mixed and revenue cannot be ordered. */
const MIXED_CURRENCY_DEFAULT_SORT_COLUMN = 'purchases'

type Props = {
    orgId: number
    template: DashboardTemplate
    status: 'loading' | 'ready' | 'error'
    rows: DailyRow[]
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    range: DateWindow
    rangeKey?: string
    goodDirectionFor: (measure: string) => GoodDirection | undefined
    onRetry: () => void
}

export const DomainsSection: React.FC<Props> = observer(
    ({ orgId, template, status, rows, domains, selectedDomainIds, range, rangeKey, goodDirectionFor, onRetry }) => {
        const appState = useService(AppState)
        const userPreferences = useUserPreferences(appState.currentUser?.id, undefined, orgId)
        const { canOpenDomain, openInDomain } = useOpenInDomainContext()
        const pinnedIds = pinnedDomainIdsFrom(userPreferences.get(PINNED_DOMAIN_IDS_PREFERENCE_PATH, []))

        const [searchTerm, setSearchTerm] = useState('')
        const [chosenSort, setChosenSort] = useState<DomainsSort | null>(null)
        const [currentPage, setCurrentPage] = useState(1)

        const comparisonWindow = useMemo(() => comparisonWindowFor(range, rangeKey), [range, rangeKey])
        const currencyCodes = useMemo(() => currenciesIn(domains, selectedDomainIds), [domains, selectedDomainIds])
        const mixedCurrency = currencyCodes.length > 1

        const domainRows = useMemo(
            () =>
                status === 'ready'
                    ? buildDomainRows({ template, rows, domains, selectedDomainIds, range, rangeKey, pinnedIds })
                    : [],
            [status, template, rows, domains, selectedDomainIds, range, rangeKey, pinnedIds.join(',')],
        )

        const defaultSortColumn =
            mixedCurrency && SINGLE_CURRENCY_CELL_KEYS.includes(TEMPLATE_SLOTS[template].defaultSort)
                ? MIXED_CURRENCY_DEFAULT_SORT_COLUMN
                : TEMPLATE_SLOTS[template].defaultSort
        const sort = useMemo<DomainsSort>(
            () =>
                chosenSort && !(mixedCurrency && SINGLE_CURRENCY_CELL_KEYS.includes(chosenSort.column))
                    ? chosenSort
                    : { column: defaultSortColumn, direction: 'desc' },
            [chosenSort, mixedCurrency, defaultSortColumn],
        )

        const sortedRows = useMemo(
            () => sortDomainRows(domainRows, sort.column, sort.direction),
            [domainRows, sort.column, sort.direction],
        )
        const trimmedSearchTerm = searchTerm.trim()
        const visibleRows = useMemo(
            () => sortedRows.filter((row) => domainMatchesSearchTerm(row.domain, trimmedSearchTerm)),
            [sortedRows, trimmedSearchTerm],
        )
        const totals = useMemo(
            () => buildTotalsRow({ domainRows, template, mixedCurrency, rows, range }),
            [domainRows, template, mixedCurrency, rows, range],
        )

        const togglePin = useCallback(
            (domainId: number) => {
                userPreferences.setDebounced(
                    PINNED_DOMAIN_IDS_PREFERENCE_PATH,
                    pinnedIds.includes(domainId) ? pinnedIds.filter((id) => id !== domainId) : [...pinnedIds, domainId],
                )
            },
            [userPreferences, pinnedIds.join(',')],
        )

        const domainPath = useCallback((domain: DashboardDomain) => `/domains/${domain.id}/notifications`, [])
        const canOpenDashboardDomain = useCallback(
            (domain: DashboardDomain) => canOpenDomain(domain.id),
            [canOpenDomain],
        )
        const openDomainDashboard = useCallback(
            (domain: DashboardDomain, event: React.MouseEvent<HTMLAnchorElement>) =>
                openInDomain(domain.id, domainPath(domain), event),
            [openInDomain, domainPath],
        )

        const changeSearchTerm = useCallback((nextSearchTerm: string) => {
            setSearchTerm(nextSearchTerm)
            setCurrentPage(1)
        }, [])

        const filters =
            status === 'loading' || (status === 'ready' && domainRows.length) ? (
                <DomainSearch
                    searchTerm={searchTerm}
                    onSearchTermChange={changeSearchTerm}
                    matchedDomainCount={visibleRows.length}
                    totalDomainCount={domainRows.length}
                    disabled={status === 'loading'}
                />
            ) : undefined

        return (
            <TableSectionWell sectionKey="domains">
                <DashboardTableCard sectionKey="domains" title="Domains" filters={filters}>
                    {status === 'error' ? (
                        <SectionError sectionName="Domains" onRetry={onRetry} />
                    ) : status === 'loading' ? (
                        <TableSkeleton
                            columns={domainsColumnFrames(template, { sort, mixedCurrency })}
                            shapes={{ domain: 'two-lines' }}
                            rowCount={TABLE_PLACEHOLDER_ROW_COUNT}
                            rowHeight={DOMAINS_TABLE_ROW_HEIGHT}
                            className={DOMAINS_TABLE_CLASS_NAME}
                            scroll={DOMAINS_TABLE_SCROLL}
                            totalsShapes={domainsTotalsShapes(template)}
                        />
                    ) : domainRows.length === 0 ? (
                        <NoDomains />
                    ) : (
                        <DomainsTable
                            template={template}
                            rows={visibleRows}
                            totals={totals}
                            domainCount={domainRows.length}
                            sentDomainCount={domainRows.filter((row) => row.sentInRange).length}
                            currencyCodes={currencyCodes}
                            comparisonWindow={comparisonWindow}
                            goodDirectionFor={goodDirectionFor}
                            sort={sort}
                            onSortChange={(nextSort) => {
                                setChosenSort(nextSort)
                                setCurrentPage(1)
                            }}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            searchTerm={trimmedSearchTerm}
                            onClearSearch={() => changeSearchTerm('')}
                            onTogglePin={togglePin}
                            onOpenDomain={openDomainDashboard}
                            domainHref={domainPath}
                            canOpenDomain={canOpenDashboardDomain}
                        />
                    )}
                </DashboardTableCard>
            </TableSectionWell>
        )
    },
)
